/**
 * Authenticated fetch for the services that call the API with raw `fetch`
 * instead of the axios client in `api.ts`.
 *
 * `api.ts` installs a response interceptor that refreshes the Entra token and
 * retries once whenever a request comes back 401. Raw `fetch` callers never
 * went through it, so any poll that happened to land after the access token
 * expired failed outright and surfaced to the user as a load error, even
 * though the session was still perfectly recoverable. This gives those callers
 * the same recovery, sharing a single in-flight refresh so a burst of parallel
 * polls triggers one refresh rather than one per request.
 *
 * Deliberately unlike the axios interceptor: this does not redirect to the
 * login page when the refresh fails. These are background polls, and
 * navigating the user out of the app from a poll is worse than returning the
 * 401 and letting the caller report it. The axios path still owns that
 * redirect.
 *
 * The retry re-sends `init` as given, which suits the JSON and GET callers
 * here; a caller streaming a request body would need to rebuild it.
 */

let refreshInFlight: Promise<string | null> | null = null;

function apiBaseUrl(): string {
  return process.env.REACT_APP_API_URL || '/api';
}

function authRefreshUrl(): string {
  const base = apiBaseUrl().replace(/\/$/, '');
  if (base.startsWith('http')) {
    return `${base}/auth/refresh`;
  }
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const path = base.startsWith('/') ? base : `/${base}`;
  return `${origin}${path}/auth/refresh`;
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;

  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const response = await fetch(
        `${authRefreshUrl()}?refresh_token=${encodeURIComponent(refreshToken)}`,
        { method: 'POST' }
      );
      if (!response.ok) return null;

      const data = await response.json();
      const accessToken: string | undefined = data?.access_token;
      if (!accessToken) return null;

      localStorage.setItem('access_token', accessToken);
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      if (data.expires_in) {
        localStorage.setItem('token_expires_at', String(Date.now() + data.expires_in * 1000));
      }
      return accessToken;
    })()
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
}

export async function authorizedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const send = (token: string | null) => {
    const headers = new Headers(init.headers);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return fetch(input, { ...init, headers });
  };

  const response = await send(localStorage.getItem('access_token'));
  if (response.status !== 401) return response;

  const refreshed = await refreshAccessToken();
  if (!refreshed) return response;

  return send(refreshed);
}
